import WobView from './wob-view'
import Backbone from 'backbone'
import _ from 'lodash'
import $ from 'jquery'
import connectorsContainer from './support/connectors/container'
import Queue from './support/raf-queue'

var Canvas = Backbone.View.extend({

  el: function () {
    return $('<div><div class="wobs">').addClass(this.className + ' canvas')
  },

  padding: 32, // matches padding set in scss

  animation_duration: 100, // matches scss

  /*
   * Initialize a canvas with options:
   *    `minWidth` an optional setting for the minWidth of this canvas
   *    `minHeight` an optional setting for the minHeight of this canvas
   */
  initialize: function (options) {
    this.views = {}
    this.queue = new Queue

    this.minWidth = options.minWidth || 0
    this.minHeight = options.minHeight || 0

    this.features = []
    this.support()

    // If options.padding is defined use that, otherwise use the default padding
    // Note: The *.css file for the canvas must have the same padding value
    this.padding = options.padding ?? this.padding

    this.listenTo(this.collection, 'add', this.add, this)
    this.listenTo(this.collection, 'reset', this.reset, this)
    this.listenTo(this.collection, 'remove', this.resize, this)
    this.listenTo(this.collection, 'remove', this.removed, this)
    this.collection.on('sync', this.resize, this)

    this.theme = options.theme
    this.background = options.background
    this.ee = options.ee
    this.user = options.user
    this.parentClass = options.parentClass || 'canvas-container'
    this.edges = options.edges
    this.duplicate = options.duplicate
    this.ignoreCopyPaste = options.ignoreCopyPaste

    /*
     * Once the canvas emits its `ready` event, after each wob is `ready`,
     * we change this flag. This is used to prevent some canvas work from being performed
     * before the canvas can support all features.
     */
    this.collection.ready = false

    this.$el.addClass('is-not-ready') // Removed once the canvas is marked `ready`

    this.collection.wobComparator = (a, b) => a.id - b.id // default based on ids
  },

  getParent: function () {
    return this.$el.parents('.' + this.parentClass)
  },

  /*
   * Executes the incoming function `fn`, after all canvas internal paints have completed.
   * Receives an optional context `ctx` for the function call
   */
  onPaint: function (fn, ctx) {
    if (!this.queue.size()) {
      // Our internal queue is empty, we can execute `fn` immediately
      fn.call(fn, ctx) // Use bound context
      return
    }
    this.queue(fn, ctx)
  },

  /*
   * Returns the bounding box of all the widget views, if
   * they have a boundingBox method
   */
  getViewsBb: function () {
    return Object.keys(this.views).reduce((bb, cid) => {
      const view = this.views[cid].view // The widget view
      const viewBb = view.boundingBox?.()
      const wob = this.views[cid].model

      const left = wob.get('left') || 0
      const top = wob.get('top') || 0

      if (viewBb) {
        bb.minX = Math.min(bb.minX, left + viewBb.x)
        bb.maxX = Math.max(bb.maxX, left + viewBb.x + viewBb.width)
        bb.minY = Math.min(bb.minY, top + viewBb.y)
        bb.maxY = Math.max(bb.maxY, top + viewBb.y + viewBb.height)
      }

      return bb
    }, {
      minX: Infinity,
      maxX: -Infinity,
      minY: Infinity,
      maxY: -Infinity
    })
  },

  /*
   * We could do this on initialize, but some calling code may want to build the canvas,
   * then bind events to it such as 'ready'. After the scalable canvas is created, code
   * most invoke this build method to add the initial collection of wobs to the canvas.
   */
  build: function () {
    var ready = () => {
      // All wobs have said they are ready, mark the collection of wobs as being ready
      // and resize the canvas.
      this.collection.ready = true
      this.resize()

      // Now make sure the wobs have been painted/positioned, then show the canvas and mark it ready
      this.queue.defer(() => {
        this.$el.removeClass('is-not-ready')
        this.trigger('ready', this)
      })
    }

    if (this.collection.length === 0) {
      ready()
    } else {
      var wobCount = 0
      this.listenTo(this, 'wob:added', function (wob) {
        if (++wobCount === this.collection.size()) {
          ready()
          this.stopListening(this, 'wob:added')
        }
      }, this)
      this.collection.each(this.add.bind(this))
    }

    // After all the wobs have been built and on the canvas, build the wobs connectors
    this.collection.each(function (wob) {
      if (wob.get('connectors')) {
        this.views[wob.cid].updateConnectors()
      }
    }.bind(this))

    return this
  },

  setWobComparator: function (compareFn) {
    this.collection.wobComparator = compareFn
  },

  /*
   * Returns a bounding box of all wobs and connectors, respecting
   * the minHeight/minWidth set on this canvas
   */
  boundingBox: function () {
    if (!this.collection.length) {
      // Ignore further calculations if we don't have wobs
      // This speeds things up a bit, but more importantly prevents
      // firefox from blowing up. https://github.com/SpiderStrategies/Scoreboard/issues/20756
      return {
        minX: 0, maxX: this.minWidth, minY: 0, maxY: this.minHeight
      }
    }
    var bb = this.collection.boundingBox()
      , connectorsBb = this.connectorsContainer.node().getBBox()
      , viewsBb = this.getViewsBb()

    return {
      minX: Math.min(bb.minX, viewsBb.minX),
      maxX: Math.max(bb.maxX, viewsBb.maxX, Math.max(connectorsBb.x, 0) + connectorsBb.width, this.minWidth),
      minY: Math.min(bb.minY, viewsBb.minY),
      maxY: Math.max(bb.maxY, viewsBb.maxY, Math.max(connectorsBb.y, 0) + connectorsBb.height, this.minHeight)
    }
  },

  /*
   * Returns the coords of some event within the canvas
   */
  coords: function (event) {
    let offset = this.$el.offset()
    return {
      left: event.pageX - this.padding - offset.left,
      top: event.pageY - this.padding - offset.top
    }
  },

  support: function () {
    // Adds the connectors container
    this.connectorsContainer = connectorsContainer(this)
  },

  removed: function (wob) {
    delete this.views[wob.cid]
  },

  resizeX: function () {
    var bounds = this.collection.boundingBox()
    this.$el.width(bounds.maxX + Math.max(0, bounds.minX))
  },

  resizeY: function () {
    var bounds = this.boundingBox()
    this.$el.height(bounds.maxY + Math.max(0, bounds.minY))
  },

  /*
   * Resize based on the width/height of the wobs
   */
  resize: function (animate) {
    if (!this.collection.ready) {
      // Ignore resizing the canvas until all wobs are ready so we don't thrash the browser
      return
    }

    var bounds = this.boundingBox()
      , x = bounds.maxX + Math.max(0, bounds.minX) // add equal padding to both sides. If the leftmost wob is 50 pixels from the left, then the rightmost wob should have 50 pixels of padding
      , y = bounds.maxY + Math.max(0, bounds.minY)

    // Bounding box returns -Infinity if there are no wobs.
    // If that's the case, don't set an explicit size on the canvas
    if (!isFinite(x)) {
      this.$el.css({
        width: '',
        height: ''
      })
    } else {
      if (animate) {
        this.$el.animate({
          height: y,
          width: x
        }, 150, 'swing')
      } else {
        this.$el.height(y)
                .width(x)
      }
    }
    this.trigger('resize', bounds)
  },

  /*
   * This is what takes a wob model, and builds a wob view from it.
   * The wob view is then added to the canvas.
   */
  add: function (wob) {
    if (wob.isNew()) {
      // Make sure this wob is stacked correctly
      this.collection.adjustZIndex(wob)
    }

    var view = new WobView({model: wob, readonly: this.readonly, canvas: this})
    this.views[wob.cid] = view

    // When a wob is added, we trigger an event saying a wob was added to the canvas.
    this.listenTo(view, 'ready', function () {
     this.trigger('wob:added', view)
    }, this)

    this.append(view)
    view.render()
  },

  /*
   * Add additional functionality into the canvas.
   * The incoming `fn` will receive the canvas
   * e.g. In a touch event world
   *
   *  ```
   *  if ('touch' in document) {
   *    canvas.use(function (canvas) {
   *      // Add touch specific events to this canvas
   *    })
   *  }
   *  ```
   */
  use: function (fn) {
    this.features.push(fn(this))
  },

  /*
   * Sets the theme class on the canvas, removing the previous class
   * and adding the new one
   */
  setTheme: function (previous, theme) {
    this.theme = theme
    this.$el.toggleClass(previous + ' ' + theme)
  },

  /*
   * Sets the background class on the canvas
   */
  setBackground: function (previous, background) {
    this.background = background
    this.$el.toggleClass((previous || '') + ' ' + (background || ''))
  },

  append: function () {
    var wobs = _.toArray(arguments)
    this.$('.wobs').append(wobs.map(function (wob) {
      return wob.el || wob
    }))
  },

  center: function () {
    var area = this.area()
    return {
      left: area.width / 2,
      top: area.height / 2
    }
  },

  area: function () {
    var area = this.$el.position()
    area.height = this.$el.height()
    area.width = this.$el.width()
    area.scrollWidth = this.el.scrollWidth
    area.scrollHeight = this.el.scrollHeight
    return area
  },

  render: function () {
    this.$el.addClass((this.theme || '') + ' ' + (this.background || ''))
    return this
  },

  /*
   * Disables all dom (pointer) events on the wob views
   */
  disableWobEvents: function () {
    _.forOwn(this.views, function (view) {
      view.disableEvents()
    })
    return this
  },

  /*
   * Enables all dom (pointer) events on the wob views
   */
  enableWobEvents: function () {
    _.forOwn(this.views, function (view) {
      view.enableEvents()
    })
    return this
  },

  reset: function () {
    var views = this.views
    Object.keys(this.views).forEach(function (key) {
      views[key].remove()
    })
    this.collection.each(this.add.bind(this))
    this.resize()
  },

  release: function () {
    _.values(this.views).forEach(function (view) {
      // Release all wob-views, without destroying the model.
      // The model is destroyed when the wobs collection is destroyed
      view.release()
    })

    this.remove()
  }

})

export default Canvas
